/**
 * Simple localStorage-based caching utility
 * Replaces the more complex cacheManager implementation
 */

/**
 * Get a value from localStorage
 * @param {string} key - The key to retrieve
 * @param {any} defaultValue - Value to return if key doesn't exist
 * @returns {any} The stored value or defaultValue
 */
export const get = (key, defaultValue = null) => {
  try {
    const value = localStorage.getItem(key);
    return value !== null ? JSON.parse(value) : defaultValue;
  } catch (error) {
    console.error(`Error getting item ${key} from localStorage:`, error);
    return defaultValue;
  }
};

/**
 * Set a value in localStorage
 * @param {string} key - The key to store
 * @param {any} value - Value to store
 */
export const set = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Error setting item ${key} in localStorage:`, error);
  }
};

/**
 * Remove a value from localStorage
 * @param {string} key - The key to remove
 */
export const remove = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error removing item ${key} from localStorage:`, error);
  }
};

/**
 * MQTT address management functions
 */

/**
 * Get MQTT addresses for a user
 * @param {string} userKey - User identifier
 * @returns {Array} Array of MQTT addresses
 */
export const getMqttAddresses = (userKey) => {
  return get(`${userKey}_mqtt_addresses`, []);
};

/**
 * Set MQTT addresses for a user
 * @param {string} userKey - User identifier
 * @param {Array} addresses - Array of MQTT addresses
 */
export const setMqttAddresses = (userKey, addresses) => {
  set(`${userKey}_mqtt_addresses`, addresses);
};

/**
 * Add an MQTT address for a user if it doesn't already exist
 * @param {string} userKey - User identifier
 * @param {string} mqttAddress - MQTT address to add
 * @returns {Array} Updated array of MQTT addresses
 */
export const addMqttAddress = (userKey, mqttAddress) => {
  const addresses = getMqttAddresses(userKey);
  if (!addresses.includes(mqttAddress)) {
    addresses.push(mqttAddress);
    setMqttAddresses(userKey, addresses);
  }
  return addresses;
};

/**
 * Get user profile data
 * @param {string} username - Username
 * @returns {Object|null} User profile data or null
 */
export const getUserProfile = (username) => {
  return get(`${username}_profile`);
};

/**
 * Set user profile data
 * @param {string} username - Username
 * @param {Object} profileData - Profile data to store
 */
export const setUserProfile = (username, profileData) => {
  set(`${username}_profile`, profileData);
};
